import React, { createContext, useContext, useState, useEffect } from "react";
import { sendEmailVerification } from "firebase/auth";
import {
  subscribeToAuthChanges,
  registerWithEmail,
  loginWithEmail,
  loginWithGoogle,
  getGoogleRedirectResult,
  logout as firebaseLogout,
  resetPassword,
  saveUser,
  updateLastLogin,
  getUserByEmail,
  getHiringManagerProfile
} from "@/config/firebase";
import { ADMIN_CONFIG } from "@/config/constants";
import { setAuthStatus, clearUserData } from "@/utils/storage";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signingIn, setSigningIn] = useState(false);
  // Track onboarding status in state to prevent flash
  // Initialize from localStorage to prevent flash on page refresh
  const [onboardingComplete, setOnboardingComplete] = useState(
    localStorage.getItem("onboardingComplete") === "true"
  );
  // Track if we're still fetching onboarding status from Firebase
  const [fetchingOnboardingStatus, setFetchingOnboardingStatus] = useState(false);

  // Hiring manager state is stored in localStorage, but also tracked in React state
  // so we can avoid stale route decisions during session changes.
  const [isHiringManager, setIsHiringManager] = useState(
    !!localStorage.getItem("hiringManagerEmail")
  );

  // Admin impersonation state - allows admin to view app as another user
  const [impersonatedUser, setImpersonatedUser] = useState(null);

  const clearHiringManagerStorage = () => {
    localStorage.removeItem("hiringManagerEmail");
    localStorage.removeItem("hiringManagerName");
    localStorage.removeItem("hiringManagerCompany");
    localStorage.removeItem("hiringManagerOrganizationId");
    localStorage.removeItem("hiringManagerAccountType");
    localStorage.removeItem("isHiringManager");
    localStorage.removeItem("justRegisteredRecruiter");
  };

  useEffect(() => {
    const unsubscribe = subscribeToAuthChanges(async (firebaseUser) => {
      if (firebaseUser) {
        console.log("AuthContext - Firebase user photoURL:", firebaseUser.photoURL);
        setUser({
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          username: firebaseUser.displayName,
          photoURL: firebaseUser.photoURL,
          emailVerified: firebaseUser.emailVerified
        });

        // If email just got verified and we have pending signup data, save user to database now
        if (firebaseUser.emailVerified) {
          const pendingData = localStorage.getItem("pendingSignupData");
          if (pendingData) {
            try {
              const { email, username, uid } = JSON.parse(pendingData);
              // Clear stale onboarding state from any previous session
              localStorage.removeItem("onboardingComplete");
              localStorage.removeItem("signupData");
              await saveUser({ email, username, full_name: username, uid });
              localStorage.removeItem("pendingSignupData");
            } catch (e) {
              console.warn("Could not save pending signup data:", e.message);
            }
          }
        }

        // If this is a hiring manager, skip all candidate-specific auth logic.
        // Validate any stored hiring-manager email against the current Firebase user.
        const storedHiringManagerEmail = localStorage.getItem("hiringManagerEmail");
        let isHM = storedHiringManagerEmail && firebaseUser.email &&
          storedHiringManagerEmail.toLowerCase() === firebaseUser.email.toLowerCase();

        // If the current user is not the same as the stored recruiter, clear stale recruiter data.
        if (storedHiringManagerEmail && firebaseUser.email &&
            storedHiringManagerEmail.toLowerCase() !== firebaseUser.email.toLowerCase()) {
          clearHiringManagerStorage();
          setIsHiringManager(false);
          isHM = false;
        }

        // If not flagged in localStorage, check Firebase in case they just signed in
        // via Google on the hiring page before localStorage was set.
        // Skip this check for admin users — they use the candidate dashboard.
        const isAdminEmail = ADMIN_CONFIG.adminEmails.some(e => e.toLowerCase() === firebaseUser.email.toLowerCase()) ||
                             ADMIN_CONFIG.viewOnlyAdminEmails?.some(e => e.toLowerCase() === firebaseUser.email.toLowerCase());
        if (!isHM && !isAdminEmail && firebaseUser.email) {
          try {
            const hmProfile = await getHiringManagerProfile(firebaseUser.email);
            if (hmProfile) {
              localStorage.setItem("hiringManagerEmail", firebaseUser.email);
              localStorage.setItem("isHiringManager", "true");
              if (hmProfile.organizationId) {
                localStorage.setItem("hiringManagerOrganizationId", hmProfile.organizationId);
              }
              isHM = true;
              setIsHiringManager(true);
            }
          } catch (e) {
            // If check fails, continue with candidate flow
          }
        }

        if (isHM) {
          console.log("✅ Hiring manager detected - skipping candidate onboarding check");
          setOnboardingComplete(false); // Keep false so candidate routes don't render
          setIsHiringManager(true);
          setFetchingOnboardingStatus(false);
          setLoading(false);
          return;
        }

        // Keep localStorage in sync for backward compatibility (candidates only)
        setAuthStatus(true);
        localStorage.setItem("signupData", JSON.stringify({
          email: firebaseUser.email,
          username: firebaseUser.displayName
        }));

        // Check if user is an admin - admins bypass onboarding
        const isAdminUser = ADMIN_CONFIG.adminEmails.some(
          adminEmail => adminEmail.toLowerCase() === firebaseUser.email.toLowerCase()
        );
        const isViewOnlyAdminUser = ADMIN_CONFIG.viewOnlyAdminEmails?.some(
          adminEmail => adminEmail.toLowerCase() === firebaseUser.email.toLowerCase()
        );

        if (isAdminUser || isViewOnlyAdminUser) {
          // Admin users bypass onboarding check
          console.log("✅ Admin user - bypassing onboarding check");
          localStorage.setItem("onboardingComplete", "true");
          setOnboardingComplete(true);
          setFetchingOnboardingStatus(false);
        } else {
          // Check if we just completed onboarding (localStorage was set by completeOnboarding)
          // This prevents race conditions with Firebase read after write
          const localOnboardingStatus = localStorage.getItem("onboardingComplete");
          console.log("🔍 AuthContext - localStorage onboardingComplete value:", localOnboardingStatus);
          if (localOnboardingStatus === "true") {
            console.log("✅ Onboarding already marked complete in localStorage - skipping Firebase check");
            setOnboardingComplete(true);
            setFetchingOnboardingStatus(false);
          } else {
            console.log("🔍 AuthContext - localStorage is NOT true, checking Firebase...");
            // Check Firebase for onboarding status
            setFetchingOnboardingStatus(true);
            try {
              console.log("AuthContext - Fetching user data for:", firebaseUser.email);
              const userData = await getUserByEmail(firebaseUser.email);
              console.log("AuthContext - User data:", userData);

              if (userData) {
                // User exists in database - check if they completed onboarding
                // Check both old format (industry) and new format (industries array)
                const hasIndustryData = userData.industry || (userData.industries && userData.industries.length > 0);
                const hasCompletedOnboarding =
                  userData.onboardingComplete === true ||
                  (hasIndustryData && userData.job_search_status); // Fallback: has profile data

                if (hasCompletedOnboarding) {
                  console.log("✅ Onboarding complete");
                  localStorage.setItem("onboardingComplete", "true");
                  setOnboardingComplete(true);
                } else {
                  console.log("❌ Onboarding NOT complete");
                  localStorage.setItem("onboardingComplete", "false");
                  setOnboardingComplete(false);
                }
              } else {
                // User doesn't exist in database yet (just signed up)
                console.log("⚠️ User not in database - needs onboarding");
                localStorage.setItem("onboardingComplete", "false");
                setOnboardingComplete(false);
              }
            } catch (error) {
              console.error("Error fetching onboarding status:", error);
              // On error, be conservative - assume onboarding not complete
              console.log("⚠️ Error checking status - defaulting to incomplete");
              localStorage.setItem("onboardingComplete", "false");
              setOnboardingComplete(false);
            }
            setFetchingOnboardingStatus(false);
          }
        }
      } else {
        // Don't clear user state if we're in the middle of signing in
        if (!signingIn) {
          setUser(null);
          localStorage.removeItem("isAuthenticated");
        }
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [signingIn]);

  // Handle redirect result when returning from Google sign-in redirect (popup-blocked fallback)
  useEffect(() => {
    getGoogleRedirectResult().then(async (result) => {
      if (result?.user) {
        await saveUser({
          email: result.user.email,
          username: result.user.displayName,
          full_name: result.user.displayName,
          uid: result.user.uid,
          photoURL: result.user.photoURL
        });
      }
    }).catch((err) => {
      console.error("Google redirect result error:", err);
    });
  }, []);

 const signup = async (email, password, username) => {
    const firebaseUser = await registerWithEmail(email, password, username);

    try {
        await sendEmailVerification(firebaseUser, {
            url: "https://hirehuddle.co/",
        });
        console.log("Verification email sent to:", firebaseUser.email);
    } catch (err) {
        console.error("sendEmailVerification failed:", err.code, err.message);
        // Don't rethrow — account is created, just flag the failure
    }

    localStorage.removeItem("onboardingComplete");
    localStorage.removeItem("signupData");
    localStorage.setItem("pendingSignupData", JSON.stringify({
        email: firebaseUser.email,
        username,
        uid: firebaseUser.uid
    }));

    return firebaseUser;
};

  const login = async (email, password) => {
    const result = await loginWithEmail(email, password);

    // Update last login in Firebase database
    await updateLastLogin(email);

    return result;
  };

  const signInWithGoogle = async () => {
    setSigningIn(true);
    try {
      const result = await loginWithGoogle();

      // Save/update user in Firebase database
      await saveUser({
        email: result.user.email,
        username: result.user.displayName,
        full_name: result.user.displayName,
        uid: result.user.uid,
        photoURL: result.user.photoURL
      });

      return result;
    } finally {
      setSigningIn(false);
    }
  };

  const logout = async () => {
    await firebaseLogout();
    clearUserData();
    clearHiringManagerStorage();
    localStorage.removeItem("signupData");
    localStorage.removeItem("onboardingComplete");
    setOnboardingComplete(false);
    setIsHiringManager(false);
  };

  // Function to mark onboarding as complete (called from Onboarding page)
  const completeOnboarding = async () => {
    // Just update state - the actual Firebase save happens in onboarding form
    localStorage.setItem("onboardingComplete", "true");
    setOnboardingComplete(true);
  };

  const forgotPassword = async (email) => {
    return resetPassword(email);
  };

  // Admin impersonation functions
  const startImpersonation = (userData) => {
    setImpersonatedUser({
      id: userData.id,
      email: userData.email,
      username: userData.username,
      full_name: userData.full_name,
      // recruiter impersonation fields
      workEmail: userData.workEmail,
      organizationId: userData.organizationId,
      isRecruiter: userData.isRecruiter || false,
    });
  };

  const stopImpersonation = () => {
    sessionStorage.removeItem("impersonatedHMEmail");
    sessionStorage.removeItem("impersonatedHMOrgId");
    setImpersonatedUser(null);
  };

  // Check if current user is a full admin (can view and modify)
  const isAdmin = user?.email ? ADMIN_CONFIG.adminEmails.some(
    adminEmail => adminEmail.toLowerCase() === user.email.toLowerCase()
  ) : false;

  // Check if current user is a view-only admin (can view but not modify)
  const isViewOnlyAdmin = user?.email ? ADMIN_CONFIG.viewOnlyAdminEmails?.some(
    adminEmail => adminEmail.toLowerCase() === user.email.toLowerCase()
  ) : false;

  // Can access admin panel (either full or view-only)
  const canAccessAdmin = isAdmin || isViewOnlyAdmin;

  const resendVerificationEmail = async () => {
    const { getAuthInstance } = await import("@/config/firebase");
    const authInstance = getAuthInstance();
    if (authInstance.currentUser) {
      await sendEmailVerification(authInstance.currentUser, {
        url: "https://hirehuddle.co/",
      });
    }
  };

  const value = {
    user,
    loading,
    fetchingOnboardingStatus,
    signup,
    login,
    signInWithGoogle,
    logout,
    forgotPassword,
    isAuthenticated: !!user,
    emailVerified: import.meta.env.DEV ? true : (user?.emailVerified ?? true),
    resendVerificationEmail,
    onboardingComplete,
    completeOnboarding,
    isAdmin,
    isViewOnlyAdmin,
    canAccessAdmin,
    isHiringManager,
    // Admin impersonation
    impersonatedUser,
    startImpersonation,
    stopImpersonation,
    isImpersonating: !!impersonatedUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
